<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import { messageStamp } from 'shared/helpers/timeHelper';

const props = defineProps({
  inboxName: { type: String, default: '' },
  inboxIcon: { type: String, default: '' },
  scheduledAt: { type: Number, default: 0 },
  triggerRules: { type: Object, default: null },
});

const { t } = useI18n();

const weekdayNames = computed(() =>
  t('CAMPAIGN.SMS.CARD.CAMPAIGN_DETAILS.WEEKDAYS', [])
);

function utcTimeToLocal(utcTime) {
  if (!utcTime) return '';
  const [h, m] = utcTime.split(':').map(Number);
  const d = new Date();
  d.setUTCHours(h, m, 0, 0);
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

const scheduleLabel = computed(() => {
  const rules = props.triggerRules;
  const type = rules?.schedule_type;
  if (!type || type === 'once') {
    return props.scheduledAt
      ? messageStamp(new Date(props.scheduledAt), 'LLL d, h:mm a')
      : '—';
  }
  const time = utcTimeToLocal(rules.schedule_time);
  if (type === 'daily')
    return t('CAMPAIGN.SMS.CARD.CAMPAIGN_DETAILS.SCHEDULE.DAILY', { time });
  if (type === 'weekly') {
    const day = weekdayNames.value[rules.schedule_day] ?? '';
    return t('CAMPAIGN.SMS.CARD.CAMPAIGN_DETAILS.SCHEDULE.WEEKLY', {
      day,
      time,
    });
  }
  if (type === 'monthly')
    return t('CAMPAIGN.SMS.CARD.CAMPAIGN_DETAILS.SCHEDULE.MONTHLY', {
      day: rules.schedule_day,
      time,
    });
  return '—';
});

const scheduleIcon = computed(() => {
  const type = props.triggerRules?.schedule_type;
  if (type === 'daily' || type === 'weekly' || type === 'monthly')
    return 'i-lucide-repeat';
  return 'i-lucide-calendar';
});
</script>

<template>
  <span class="flex-shrink-0 text-sm text-n-slate-11 whitespace-nowrap">
    {{ t('CAMPAIGN.SMS.CARD.CAMPAIGN_DETAILS.SENT_FROM') }}
  </span>
  <div class="flex items-center gap-1.5 flex-shrink-0">
    <Icon :icon="inboxIcon" class="flex-shrink-0 text-n-slate-12 size-3" />
    <span class="text-sm font-medium text-n-slate-12">{{ inboxName }}</span>
  </div>

  <span class="flex-shrink-0 text-sm text-n-slate-11 whitespace-nowrap">
    {{ t('CAMPAIGN.SMS.CARD.CAMPAIGN_DETAILS.ON') }}
  </span>
  <div class="flex items-center gap-1 flex-1 min-w-0">
    <span class="size-3 text-n-slate-9 shrink-0" :class="[scheduleIcon]" />
    <span class="text-sm font-medium truncate text-n-slate-12">{{
      scheduleLabel
    }}</span>
  </div>
</template>
